// Models
// Utilities
import remove from "lodash/remove";
/**
 * Abstract class that handles event pub/sub for all Stores.
 * Stores need to be able to publish change events to all subscribers.
 */ export class EventPublishingStore {
    /**
     * Inject (or clear) the publish-frequency monitor used by every store's publish path. Called
     * once by app bootstrap after it has constructed and configured the monitor; pass `undefined`
     * on teardown to release it.
     * @param monitor - The monitor instance to use, or `undefined` to disable instrumentation.
     */ static setPublishFrequencyMonitor(monitor) {
        const previous = EventPublishingStore.publishFrequencyMonitor;
        if (previous !== undefined && previous !== monitor) {
            // Release the outgoing monitor so its pending flush timers are cancelled and it stops
            // emitting diagnostics once it is no longer the injected instance.
            previous.dispose();
        }
        EventPublishingStore.publishFrequencyMonitor = monitor;
    }
    /**
     * Get the number of subscribers
     */ getSubscriberCount() {
        return this.listeners.length;
    }
    /**
     * Subscribe to this Store with a callback method
     * @param callback - Method to be called on Store changes
     */ subscribeToStore(callback) {
        this.listeners.push(callback);
    }
    /**
     * Unsubscribe from this Store
     * @param callback - Method to remove from list of callbacks
     */ unsubscribeFromStore(callback) {
        remove(this.listeners, (cb)=>{
            return cb === callback;
        });
    }
    /**
     * Publish a 'store changed' event, which will call all subscribers' callback methods.
     */ publishStoreChangedEvent() {
        const monitor = EventPublishingStore.publishFrequencyMonitor;
        if (monitor?.isEnabled) {
            monitor.recordPublish(this.getStoreNameForTelemetry(), this.listeners.length);
        }
        for (const callback of this.listeners){
            callback?.();
        }
    }
    /**
     * Best-effort store name used for publish-frequency telemetry. Stores that implement IStore
     * expose a `name` getter; for anything else we fall back to the constructor name.
     */ getStoreNameForTelemetry() {
        const maybeNamed = this;
        if (typeof maybeNamed.name === "string") {
            return maybeNamed.name;
        }
        return this.constructor.name;
    }
    constructor(){
        this.listeners = [];
    }
}
